import { ChangeEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsAutoComplete } from '@components'
import { Search } from '@components/search'
import { templatesWithFolder } from '@global/api'
import { Template } from '@templates/types'
import * as css from './style'
import { t } from '@lingui/core/macro'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'

interface IAutoCompleteProps {
  onSelected: ((item: string) => void) | ((item: any[]) => void)
  options: object
  selected: string | string[]
  isDisabled: boolean
}

interface ISearchProps {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  defaultValue?: string
}

interface IProps {
  templateSearchProps: Partial<IAutoCompleteProps>
  pendingStatusSearchProps: IAutoCompleteProps
  searchProps: ISearchProps
}

interface TemplateOption {
  label: string
  value: string
}

export const Toolbar = ({ templateSearchProps, pendingStatusSearchProps, searchProps }: IProps) => {
  const { onChange, defaultValue } = searchProps
  const { selected, onSelected } = templateSearchProps
  const [DATASETS_PAGE_ENABLED = false] = useSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const {
    options: pOptions,
    onSelected: pOnSelected,
    selected: pSelected,
    isDisabled: pIsDisabled = false,
  } = pendingStatusSearchProps

  const { data: templateOptions = [], isLoading } = useQuery({
    queryKey: ['template-with-folder-select-options'],
    queryFn: ({ signal }) => templatesWithFolder({ signal }),
    refetchOnWindowFocus: false,
    staleTime: 5 * 3600 * 1000, // 5 hours
    cacheTime: 10 * 3600 * 1000, // 10 hours
    select: (data: Template[]): TemplateOption[] => {
      return data.map(temp => {
        const value = 'id' in temp ? String(temp.id) : ''
        const label = 'name' in temp ? temp.name : ''

        return { value, label }
      })
    },
  })

  return (
    <css.Toolbar>
      <Search onChange={onChange} defaultValue={defaultValue} />
      <CvsAutoComplete
        isClearable
        label={DATASETS_PAGE_ENABLED ? 'Dataset' : 'Template'}
        margin="dense"
        noMargin={true}
        disabled={!isLoading && templateOptions?.length === 0}
        isLoading={isLoading}
        onSelected={onSelected}
        style={css.selectStyle}
        options={templateOptions as never[]}
        selected={String(selected)}
        allowMissingValueErrors={false}
        skin="white"
      />
      <CvsAutoComplete
        isClearable
        isMulti
        label={t`Status`}
        margin="dense"
        noMargin={true}
        disabled={pIsDisabled}
        onSelected={pOnSelected}
        options={pOptions as never[]}
        selected={formatSelectedOptions(true, pSelected, pOptions)}
        style={{ ...css.selectStyle, maxWidth: 260 }}
        allowMissingValueErrors={false}
        skin="white"
      />
    </css.Toolbar>
  )
}
